import { useEffect, useRef, useState } from 'react';

import { ApiError } from '../../lib/api';
import { pollUntil, uploadArtwork } from '../../lib/upload';
import { Loader } from './Loader';

type Phase = 'idle' | 'uploading' | 'processing' | 'failed';

interface Props {
  /** Which thing the cover belongs to. Decides the endpoint and the prefix. */
  subject: 'tracks' | 'collections';
  subjectId: number;
  /** The current cover, or null. */
  artwork: string | null;
  /** Reads the fresh cover back, so the poll knows when the worker finished. */
  refetch: () => Promise<{ artwork: string | null }>;
  editable: boolean;
  onUploaded: () => void;
  size?: 'lg' | 'md';
  label?: string;
}

/**
 * A cover, clickable if it is yours.
 *
 * One component for tracks and albums, because the two differ only in which
 * endpoint they post to. The four states are the ones every upload in this
 * codebase has (§4.1): choose, upload, wait for the worker, done.
 *
 * The processing wait is short - it is one image - but it is still a wait, and
 * a cover that silently does not change for ten seconds reads as a failure.
 */
export function ArtworkPicker({
  subject,
  subjectId,
  artwork,
  refetch,
  editable,
  onUploaded,
  size = 'lg',
  label = 'cover',
}: Props) {
  const input = useRef<HTMLInputElement>(null);

  const [phase, setPhase] = useState<Phase>('idle');
  const [preview, setPreview] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  /*
   * Object URLs are revoked, always.
   *
   * `createObjectURL` pins the file in memory until something revokes it, and
   * "something" is only ever this code. Unmounting mid-upload - navigating away
   * while the worker runs - is the case that leaks otherwise, and it is the
   * common one.
   */
  useEffect(
    () => () => {
      if (preview) URL.revokeObjectURL(preview);
    },
    [preview],
  );

  /** Drop the local preview so the served image takes over. */
  const clearPreview = () =>
    setPreview((current) => {
      if (current) URL.revokeObjectURL(current);

      return null;
    });

  async function choose(file: File) {
    setFailure(null);

    /*
     * A local preview the instant a file is chosen.
     *
     * The round trip takes long enough that without it the page looks inert
     * while something is plainly happening - and it is honest, because this
     * really is the image they picked.
     */
    setPreview(URL.createObjectURL(file));
    setPhase('uploading');

    try {
      await uploadArtwork(subject, subjectId, file);
      setPhase('processing');

      /*
       * Wait for a cover that is not the one already there.
       *
       * Comparing against the previous value rather than merely checking for a
       * non-null one: replacing an existing cover writes to the same key, so
       * "artwork is set" was already true before the job ran and the poll would
       * return immediately with the old image still on screen.
       */
      await pollUntil(
        refetch,
        (fresh) => fresh.artwork !== null && fresh.artwork !== artwork,
        { intervalMs: 1200, timeoutMs: 45_000 },
      );

      /*
       * The preview goes, and the real cover takes over.
       *
       * Not cosmetic. Without this the blob URL stays on screen forever - it
       * looks correct, because it is the same picture - and the served image is
       * never actually shown, so a failure to store it would be invisible until
       * somebody reloaded. It also leaks the file for as long as the page
       * lives.
       */
      clearPreview();
      setPhase('idle');
      onUploaded();
    } catch (error) {
      setPhase('failed');
      clearPreview();
      setFailure(error instanceof ApiError ? error.message : 'That image could not be uploaded.');
    }
  }

  const src = preview ?? artwork;
  const busy = phase === 'uploading' || phase === 'processing';
  const box = size === 'lg' ? 'h-44 w-44' : 'h-28 w-28';

  const cover = (
    <div
      className={`relative ${box} shrink-0 overflow-hidden rounded-[var(--radius-card)] border border-line bg-raised`}
      data-artwork={subjectId}
    >
      {src ? (
        <img src={src} alt="" className="h-full w-full object-cover" />
      ) : (
        // A patterned placeholder rather than a broken-image icon or an empty
        // box, so something with no cover still reads as a thing.
        <div
          className="h-full w-full"
          style={{
            backgroundImage: 'repeating-linear-gradient(45deg, #151530 0 10px, #101024 10px 20px)',
          }}
        />
      )}

      {busy && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-app/70">
          <Loader size="sm" />
          <span className="meta-caps">{phase === 'uploading' ? 'Uploading' : 'Processing'}</span>
        </div>
      )}

      {editable && !busy && (
        <span className="absolute inset-x-0 bottom-0 bg-app/80 py-1.5 text-center text-xs text-soft">
          {artwork ? `Change ${label}` : `Add ${label}`}
        </span>
      )}
    </div>
  );

  if (!editable) {
    return cover;
  }

  return (
    <div className="flex flex-col gap-2">
      <input
        ref={input}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="sr-only"
        data-artwork-input={subjectId}
        disabled={busy}
        onChange={(event) => {
          const file = event.target.files?.[0];

          if (file) void choose(file);
        }}
      />

      <button
        type="button"
        disabled={busy}
        onClick={() => input.current?.click()}
        aria-label={artwork ? `Change ${label}` : `Add ${label}`}
        className="rounded-[var(--radius-card)] transition-opacity hover:opacity-90 disabled:cursor-not-allowed"
      >
        {cover}
      </button>

      {failure && <p className={`text-xs text-negative ${size === 'lg' ? 'max-w-44' : 'max-w-28'}`}>{failure}</p>}
    </div>
  );
}
